export type TimeUnits = "milliseconds" | "seconds" | "minutes" | "hours" | "days" | "months" | "years";
export const months = ['Jan' , 'Feb' , 'Mar' , 'Apr' , 'May' , 'June' ,
    'July' , 'Aug' , 'Sep' , 'Oct' , 'Nov' , 'Dec'];
type Units = TimeUnits | "bytes" | "terabytes" | "gigabytes";

const conversionTable = [
    { from: 'seconds', to: 'minutes', conversionFactor: 60 },
    { from: 'bytes', to: 'terabytes', conversionFactor: 1024 * 1024 * 1024 * 1024 },
    { from: 'bytes', to: 'gigabytes', conversionFactor: 1024 * 1024 * 1024 },
    { from: 'months', to: 'years', conversionFactor: 12 }
];

export const convert = (from: Units, to: Units, amount: number, precision: number = 0) => {
    const conversionUp = conversionTable.find(x => x.from === from && x.to === to);
    const conversionDown = conversionTable.find(x => x.to === from && x.from === to);

    if(conversionUp) {
        const res = round(amount/conversionUp.conversionFactor, precision);
        return Number.isNaN(res) ? null : res; 
    }

    if(conversionDown){
        const res = round(amount*conversionDown.conversionFactor, precision);
        return Number.isNaN(res) ? null : res; 
    }
    
    console.log(`conversion not found from ${from} to ${to}. Returning input value`);
    return amount;
}

export const round = (value, precision) => {
    const multiplier = Math.pow(10, precision || 0);
    return Math.floor(value * multiplier) / multiplier;
}

export const formatDuration = (value: number, units:  'minutes' | 'seconds') => {
    if(value === null || value === undefined){
        return null;
    }

    let minPartRaw: number = null;
    let minPart: number = null;

    if(units === 'minutes'){
        minPartRaw = value;
        minPart = Math.floor(value);
    } else {
        minPartRaw = convert('seconds', 'minutes', value, 2);
        minPart = Math.floor(minPartRaw);
    }

    return `${minPart} mins`;
}

export const convertToPercent = (part: number, total: number, precision: number = 0): number => {
    if(total === 0){
        return 0;
    }
    return round((part / total) * 100, precision);
}

export const convertDateToMonth = (date: Date, includeYearAtBoundary: boolean = true): string => {
    if(includeYearAtBoundary && (date.getMonth() === 0 || date.getMonth() === 11)) {
        return `${months[date.getMonth()]} ${date.getFullYear()}`;
    }
    return months[date.getMonth()];
}

export const convertMonthKeyToDate = (monthKey: string): Date => {
    const [year, month] = monthKey.split('/').map(x => parseInt(x));
    return new Date(year, month - 1);
}

export type StorageUnit = 'GB' | 'TB' | 'PB';

const BYTES_IN: Record<StorageUnit, number> = {
    GB: 1024 ** 3,
    TB: 1024 ** 4,
    PB: 1024 ** 5,
};

/**
 * Converts a byte figure to a specific storage unit. Defaults to two decimals so a value smaller than
 * one of that unit (e.g. a modest tenant shown on a chart scaled to TB) still reads sensibly.
 */
export const convertBytesToUnit = (bytes: number, unit: StorageUnit, precision: number = 2): number =>
    round((bytes || 0) / BYTES_IN[unit], precision);

/**
 * Picks a human-friendly storage unit for a byte figure - GB below a terabyte, then TB, then PB - and
 * returns the value scaled to it. GB is rounded to whole numbers to match the dashboard's existing
 * figures; TB and PB keep one decimal so they do not read as a flat, lossy integer.
 */
export const scaleBytesToStorageUnit = (bytes: number): { value: number; unit: StorageUnit } => {
    const b = bytes || 0;
    if (b >= BYTES_IN.PB) {
        return { value: convertBytesToUnit(b, 'PB', 1), unit: 'PB' };
    }
    if (b >= BYTES_IN.TB) {
        return { value: convertBytesToUnit(b, 'TB', 1), unit: 'TB' };
    }
    return { value: convertBytesToUnit(b, 'GB', 0), unit: 'GB' };
};

/** Reconstructs a byte figure from a value already expressed in a storage unit (the inverse of
 *  convertBytesToUnit), so a value scaled for a chart can be re-formatted into a different unit. */
export const unitToBytes = (value: number, unit: StorageUnit): number => (value || 0) * BYTES_IN[unit];

// Largest-first so the first unit a figure clears is the one it reads in; includes MB/KB (which the
// chart's GB/TB/PB scale does not) so a tiny tenant's contribution is shown rather than rounded away.
const HUMAN_BYTE_UNITS: { unit: string; bytes: number }[] = [
    { unit: 'PB', bytes: 1024 ** 5 },
    { unit: 'TB', bytes: 1024 ** 4 },
    { unit: 'GB', bytes: 1024 ** 3 },
    { unit: 'MB', bytes: 1024 ** 2 },
    { unit: 'KB', bytes: 1024 },
];

/**
 * Formats a byte figure in the largest unit where it reads as at least one (down to KB, then whole
 * bytes), keeping up to `precision` decimals with trailing zeros trimmed - so 5 MB shows as "5 MB" and
 * 4.77 MB as "4.77 MB". Used by the stacked-chart "Other" breakdown so a tenant far smaller than the
 * chart unit (e.g. a few MB on a GB chart) still shows its real contribution instead of rounding to 0.
 */
export const formatBytesHuman = (bytes: number, precision: number = 2): string => {
    const b = bytes || 0;
    if (b < HUMAN_BYTE_UNITS[HUMAN_BYTE_UNITS.length - 1].bytes) {
        return `${Math.round(b)} B`;
    }
    const chosen = HUMAN_BYTE_UNITS.find((u) => b >= u.bytes) ?? HUMAN_BYTE_UNITS[HUMAN_BYTE_UNITS.length - 1];
    const value = b / chosen.bytes;
    return `${value.toLocaleString(undefined, { maximumFractionDigits: precision })} ${chosen.unit}`;
};